import { useEffect, useState, useCallback } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import {
  Newspaper,
  ExternalLink,
  Star,
  Loader2,
  ChevronLeft,
  ChevronRight,
  AlertCircle,
} from "lucide-react";
import { useLanguage } from "../contexts/LanguageContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const PAGE_SIZE = 12;

function formatDate(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    return d.toLocaleDateString(undefined, {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  } catch {
    return iso.slice(0, 10);
  }
}

export default function News() {
  const { lang } = useLanguage();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [total, setTotal] = useState(0);

  // SEO / AEO: dynamic <title> + meta description + canonical
  useEffect(() => {
    const prevTitle = document.title;
    document.title =
      "Ujjain Simhastha 2028 News & Updates | Ujjain Maha Kumbh";
    const setMeta = (name, content, isProperty = false) => {
      const attr = isProperty ? "property" : "name";
      let tag = document.querySelector(`meta[${attr}="${name}"]`);
      const created = !tag;
      if (!tag) {
        tag = document.createElement("meta");
        tag.setAttribute(attr, name);
        document.head.appendChild(tag);
      }
      const prev = tag.getAttribute("content");
      tag.setAttribute("content", content);
      return () => {
        if (created) tag.remove();
        else if (prev !== null) tag.setAttribute("content", prev);
      };
    };
    const desc =
      "Curated daily news updates about Ujjain Maha Kumbh 2028 (Simhastha Kumbh Mela) from verified Indian publishers. Auto-fetched daily, editor-reviewed, available in 22+ Indian languages.";
    const restorers = [
      setMeta("description", desc),
      setMeta("og:title", "Ujjain Simhastha 2028 News & Updates", true),
      setMeta("og:description", desc, true),
      setMeta("og:url", "https://ujjainmahakumbh.com/news", true),
      setMeta("twitter:title", "Ujjain Simhastha 2028 News & Updates"),
      setMeta("twitter:description", desc),
    ];
    // Canonical
    let canonical = document.querySelector('link[rel="canonical"]');
    const prevCanonical = canonical?.getAttribute("href");
    if (!canonical) {
      canonical = document.createElement("link");
      canonical.setAttribute("rel", "canonical");
      document.head.appendChild(canonical);
    }
    canonical.setAttribute("href", "https://ujjainmahakumbh.com/news");
    return () => {
      document.title = prevTitle;
      restorers.forEach((r) => r && r());
      if (prevCanonical) canonical.setAttribute("href", prevCanonical);
    };
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const { data } = await axios.get(`${API}/news`, {
        params: { lang: lang || "en", page, limit: PAGE_SIZE },
      });
      setItems(data.items || []);
      setPages(data.pages || 1);
      setTotal(data.total || 0);
    } catch (e) {
      setError(e?.response?.data?.detail || e.message || "Failed to load news");
    } finally {
      setLoading(false);
    }
  }, [lang, page]);

  useEffect(() => {
    load();
  }, [load]);

  // Dynamic ItemList JSON-LD for the currently loaded news items (AEO/GEO)
  useEffect(() => {
    if (!items.length) return;
    const script = document.createElement("script");
    script.type = "application/ld+json";
    script.id = "news-itemlist-jsonld";
    const payload = {
      "@context": "https://schema.org",
      "@type": "ItemList",
      name: "Ujjain Simhastha 2028 — Latest News Items",
      itemListOrder: "https://schema.org/ItemListOrderDescending",
      numberOfItems: items.length,
      itemListElement: items.slice(0, 20).map((n, i) => ({
        "@type": "ListItem",
        position: i + 1,
        item: {
          "@type": "NewsArticle",
          headline: n.display_title || n.title,
          description: n.display_snippet || n.snippet,
          url: n.link,
          datePublished: n.published_at,
          publisher: { "@type": "Organization", name: n.source || "Unknown" },
          inLanguage: lang || "en",
          isBasedOn: n.link,
        },
      })),
    };
    script.textContent = JSON.stringify(payload);
    // Replace any previous instance
    const prev = document.getElementById("news-itemlist-jsonld");
    if (prev) prev.remove();
    document.head.appendChild(script);
    return () => script.remove();
  }, [items, lang]);

  return (
    <div
      className="min-h-screen pt-28 pb-20"
      style={{ background: "#1c1917" }}
      data-testid="news-page"
    >
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="mb-10"
        >
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-saffron/10 border border-saffron/20 mb-5">
            <Newspaper size={14} className="text-saffron" />
            <span className="text-saffron text-xs font-body uppercase tracking-widest">
              News & Updates
            </span>
          </div>
          <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl text-white font-bold leading-tight mb-4">
            Simhastha 2028 News
          </h1>
          <p className="text-white/60 text-base font-body max-w-2xl leading-relaxed">
            Curated news updates about Ujjain Maha Kumbh 2028 from verified Indian
            news publishers. Auto-fetched daily and reviewed by our editors.
          </p>
        </motion.div>

        {/* Error */}
        {error && (
          <div
            className="mb-6 p-3 rounded-xl bg-red-500/10 border border-red-500/30 text-red-300 text-sm font-body flex items-start gap-2"
            data-testid="news-error"
          >
            <AlertCircle size={16} className="mt-0.5 flex-shrink-0" />
            <span>{error}</span>
          </div>
        )}

        {/* Loading */}
        {loading && (
          <div className="flex items-center justify-center py-16 text-white/50">
            <Loader2 className="animate-spin" size={22} />
            <span className="ml-2 text-sm font-body">Loading news…</span>
          </div>
        )}

        {/* Empty */}
        {!loading && items.length === 0 && !error && (
          <div
            className="text-center py-16 text-white/50 text-sm font-body"
            data-testid="news-empty"
          >
            No published news items yet. Please check back soon.
          </div>
        )}

        {/* Grid */}
        {!loading && items.length > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {items.map((n, i) => (
              <motion.a
                key={n.id}
                href={n.link}
                target="_blank"
                rel="noopener noreferrer"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, delay: Math.min(i * 0.04, 0.5) }}
                className="group flex flex-col rounded-2xl p-5 border border-white/10 hover:border-saffron/40 transition-all duration-300 hover:-translate-y-1"
                style={{
                  background:
                    "linear-gradient(135deg, rgba(255,153,51,0.04) 0%, rgba(28,28,30,0.6) 60%)",
                  boxShadow: "0 10px 30px rgba(0,0,0,0.3)",
                }}
                data-testid={`news-card-${i}`}
              >
                {/* Featured badge */}
                <div className="flex items-center justify-between mb-3">
                  <span
                    className="px-2 py-0.5 rounded-full text-[10px] font-body uppercase tracking-widest"
                    style={{
                      background: "rgba(255,153,51,0.1)",
                      color: "#FF9933",
                      border: "1px solid rgba(255,153,51,0.25)",
                    }}
                  >
                    {n.source}
                  </span>
                  {n.featured && (
                    <span
                      className="inline-flex items-center gap-1 text-[10px] font-body text-amber-300"
                      title="Featured"
                    >
                      <Star size={12} fill="currentColor" />
                      Featured
                    </span>
                  )}
                </div>

                <h3
                  className="font-heading font-semibold text-white text-lg leading-snug mb-2 line-clamp-3"
                  data-testid={`news-title-${i}`}
                >
                  {n.display_title}
                </h3>

                <p className="text-white/55 text-sm font-body leading-relaxed line-clamp-3 mb-4 flex-1">
                  {n.display_snippet}
                </p>

                <div className="flex items-center justify-between pt-3 border-t border-white/5">
                  <span className="text-white/40 text-xs font-body">
                    {formatDate(n.published_at)}
                  </span>
                  <span className="inline-flex items-center gap-1 text-saffron text-xs font-body font-medium group-hover:gap-2 transition-all">
                    Read on source
                    <ExternalLink size={12} />
                  </span>
                </div>
              </motion.a>
            ))}
          </div>
        )}

        {/* Pagination */}
        {!loading && pages > 1 && (
          <div className="mt-10 flex items-center justify-center gap-3">
            <button
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page === 1}
              className="p-2 rounded-full border border-white/10 text-white/70 hover:text-saffron hover:border-saffron/40 disabled:opacity-30 disabled:cursor-not-allowed transition-all"
              data-testid="news-prev-page"
            >
              <ChevronLeft size={16} />
            </button>
            <span className="text-white/50 text-sm font-body" data-testid="news-page-info">
              Page {page} of {pages} · {total} items
            </span>
            <button
              onClick={() => setPage((p) => Math.min(pages, p + 1))}
              disabled={page >= pages}
              className="p-2 rounded-full border border-white/10 text-white/70 hover:text-saffron hover:border-saffron/40 disabled:opacity-30 disabled:cursor-not-allowed transition-all"
              data-testid="news-next-page"
            >
              <ChevronRight size={16} />
            </button>
          </div>
        )}

        {/* Disclaimer */}
        <p className="mt-12 text-center text-[11px] text-white/35 font-body">
          Translations are AI-generated and provided for convenience. For the
          most accurate reporting, please read the article on the source site.
        </p>
      </div>
    </div>
  );
}
